import { storeWrapper } from '../storeWrapper'
import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'

const initialState = {
  overtimesTableUpdate: 0
}

export const useOvertimesStore = storeWrapper('useOvertimesStore', (set, get) => ({
  ...initialState,
  createOvertimeClaim: async ({
    amount,
    claimRecipientUserId,
    clientNo,
    createdBy,
    createdDate,
    description,
    hours,
    incurredDate,
    matterNo,
    overtimeClaimTypeId,
    status,
    updatedBy,
    updatedDate
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `createOvertimeClaim-${claimRecipientUserId}-${incurredDate}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'accounts',
              endpoint: `/OvertimeClaim/Create`,
              data: {
                amount: amount,
                claimRecipientUserId: claimRecipientUserId,
                clientNo: clientNo,
                createdBy: createdBy,
                createdDate: createdDate,
                description: description,
                hours: hours,
                incurredDate: incurredDate,
                matterNo: matterNo,
                overtimeClaimTypeId: overtimeClaimTypeId,
                status: status,
                updatedBy: updatedBy,
                updatedDate: updatedDate
              }
            })
            set({ overtimesTableUpdate: get().overtimesTableUpdate + 1 })
            resolve(response)
          } catch (error) {
            console.log('Error creating overtime claim:', error)
            reject(error)
          }
        }
      })
    })
  },
  updateOvertimeClaim: async ({
    amount,
    claimRecipientUserId,
    clientNo,
    createdBy,
    createdDate,
    description,
    hours,
    incurredDate,
    isSubmitter,
    matterNo,
    overtimeClaimId,
    overtimeClaimTypeId,
    status,
    updatedBy,
    updatedDate
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: isSubmitter
          ? `updateOvertimeClaimBySubmitter-${claimRecipientUserId}-${incurredDate}`
          : `updateOvertimeClaimByApprover-${claimRecipientUserId}-${incurredDate}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'accounts',
              endpoint: isSubmitter ? `/OvertimeClaim/UpdateBySubmitter` : `/OvertimeClaim/UpdateByApprover`,
              data: {
                amount: amount,
                claimRecipientUserId: claimRecipientUserId,
                clientNo: clientNo,
                createdBy: createdBy,
                createdDate: createdDate,
                description: description,
                hours: hours,
                incurredDate: incurredDate,
                matterNo: matterNo,
                overtimeClaimId: overtimeClaimId,
                overtimeClaimTypeId: overtimeClaimTypeId,
                status: status,
                updatedBy: updatedBy,
                updatedDate: updatedDate
              }
            })
            set({ overtimesTableUpdate: get().overtimesTableUpdate + 1 })
            resolve(response)
          } catch (error) {
            console.log('Error updating overtime claim:', error)
            reject(error)
          }
        }
      })
    })
  },
  deleteOvertimeClaim: async ({ overtimeClaimId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `deleteOvertimeClaim-${overtimeClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'DELETE',
              service: 'accounts',
              endpoint: `/OvertimeClaim/Delete/`,
              data: { overtimeClaimId: overtimeClaimId },
              sendAsUrlParams: true
            })

            set({ overtimesTableUpdate: get().overtimesTableUpdate + 1 })
            resolve(response)
          } catch (error) {
            console.log('Error deleting overtime claim:', error)
            reject(error)
          }
        }
      })
    })
  },
  updateOvertimeClaimStatuses: async ({ status, overtimeClaimIds }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `updateOvertimeClaimStatuses`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'accounts',
              endpoint: `/OvertimeClaim/UpdateOvertimeClaimStatuses?status=${status}`,
              data: overtimeClaimIds
            })

            set({ overtimesTableUpdate: get().overtimesTableUpdate + 1 })
            resolve(response)
          } catch (error) {
            console.log('Error updating overtime claim statuses:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchAllExtendedOvertimeClaims: async ({
    amountNumberComparisonOperator,
    amountValue,
    claimRecipientUserIds,
    clientNo,
    description,
    incurredDate,
    incurredDateComparisonOperator,
    isSubmission,
    matterNo,
    orderByColumns,
    overtimeClaimTypeIds,
    pageSize,
    startAtIndex,
    statuses
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchAllExtendedOvertimeClaims`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'accounts',
              endpoint: `/OvertimeClaim/GetAllExtendedBySearchCriteria`,
              data: {
                ApproversOrSubmitters: isSubmission ? 'Submitters' : 'Approvers',
                Amount:
                  amountValue == null
                    ? null
                    : {
                        Value: amountValue,
                        NumberComparisonOperator: amountNumberComparisonOperator ?? 0
                      },
                ClaimRecipientUserIds: claimRecipientUserIds,
                ClientNo: clientNo,
                Description: description,
                IncurredDate:
                  incurredDate == null
                    ? null
                    : {
                        Date: incurredDate,
                        DateComparisonOperator: incurredDateComparisonOperator ?? 0
                      },
                MatterNo: matterNo,
                OrderByColumns: orderByColumns,
                OvertimeClaimTypeIds: overtimeClaimTypeIds,
                PageSize: pageSize,
                StartAtIndex: startAtIndex,
                Statuses: statuses
              }
            })

            resolve(response)
          } catch (error) {
            console.log('Error fetching extended overtime claims:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchAllExtendedOvertimeClaimsAsCSV: async ({
    amountNumberComparisonOperator,
    amountValue,
    claimRecipientUserIds,
    clientNo,
    description,
    incurredDate,
    incurredDateComparisonOperator,
    isSubmission,
    matterNo,
    orderByColumns,
    overtimeClaimTypeIds,
    pageSize,
    startAtIndex,
    statuses
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchAllExtendedOvertimeClaimsAsCSV`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'accounts',
              endpoint: `/OvertimeClaim/GetAllExtendedBySearchCriteriaAsCsv`,
              data: {
                ApproversOrSubmitters: isSubmission ? 'Submitters' : 'Approvers',
                Amount:
                  amountValue == null
                    ? null
                    : {
                        Value: amountValue,
                        NumberComparisonOperator: amountNumberComparisonOperator ?? 0
                      },
                ClaimRecipientUserIds: claimRecipientUserIds,
                ClientNo: clientNo,
                Description: description,
                IncurredDate:
                  incurredDate == null
                    ? null
                    : {
                        Date: incurredDate,
                        DateComparisonOperator: incurredDateComparisonOperator ?? 0
                      },
                MatterNo: matterNo,
                OrderByColumns: orderByColumns,
                OvertimeClaimTypeIds: overtimeClaimTypeIds,
                PageSize: pageSize,
                StartAtIndex: startAtIndex,
                Statuses: statuses
              }
            })

            const blob = new Blob([response], { type: 'text/csv' })

            const blobUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = 'my_overtime_export.csv'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            URL.revokeObjectURL(blobUrl)

            resolve(response)
          } catch (error) {
            console.log('Error fetching extended overtime claims as CSV:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchOvertimeDetails: async ({ overtimeClaimId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchOvertimeDetails-${overtimeClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'accounts',
              endpoint: `/OvertimeClaim/GetExtendedById`,
              data: { overtimeClaimId: overtimeClaimId }
            })
            resolve(response)
          } catch (error) {
            console.log('Error fetching overtime details:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchDraftOvertimeClaims: async ({ pageSize, startAtIndex, status }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchDraftOvertimeClaims`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'accounts',
              endpoint: `/OvertimeClaim/GetAllExtendedBySearchCriteria`,
              data: {
                pageSize: pageSize,
                startAtIndex: startAtIndex,
                statuses: [status],
                ApproversOrSubmitters: 'Submitters'
              }
            })
            resolve(response)
          } catch (error) {
            console.log('Error fetching draft overtime claims:', error)
            reject(error)
          }
        }
      })
    })
  },
  reset: () => set(initialState)
}))
